import {
    MOBILE_PROMO_CODE_PATTERN,
    MobileOfferCatalogEntry,
    MobileOfferProvider,
    MobilePromoCode,
    MobilePromoCodeGoogleOption,
    MobilePromoCodePlanOptions,
    MobilePromoCodePlanTargets,
    MobileRetentionStripeCoupon,
} from '@numeo/types'
import { MobilePlan } from '@numeo/types-core'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { mobileOfferError, mobileOffersApi } from './api'
import { CodesChip, OfferButton, offerFieldClass, OfferNotice, storeMark } from './ui'
import { offerLinkClass, offerPlans, planLabels } from './offerLabels'
import { usePollWhile } from './usePollWhile'

const months = (count: number) => `${count} ${count === 1 ? 'month' : 'months'}`
const offerLabel = (offer: MobileOfferCatalogEntry) =>
    `${offer.terms.percentOff}% off · ${months(offer.terms.discountedPeriods)}${offer.terms.environment === 'sandbox' ? ' (test purchases)' : ''}`
/**
 * Whom a code with this iOS offer reaches — the offer's own audience (Create offer → Customer audience). A current
 * subscriber who types the code gets the discount on their next renewal; a new user on the purchase they make.
 */
const appleReach = (offer: MobileOfferCatalogEntry): string => {
    const audience = offer.terms.customerEligibilities
    if (!audience) return 'new & current subscribers'
    const parts = [audience.includes('NEW') ? 'new' : '', audience.includes('EXISTING') ? 'current' : '', audience.includes('EXPIRED') ? 'expired' : ''].filter(
        Boolean
    )
    return parts.length ? `${parts.join(' & ')} subscribers` : 'nobody'
}
/** Play can't discount a base plan the user owns: a regular offer reaches new users, a retention base-plan offer current subscribers. */
const googleReach = (reaches: MobilePromoCodeGoogleOption['reaches']): string => (reaches === 'current_subscribers' ? 'current subscribers' : 'new users')
const couponLabel = (coupon: MobileRetentionStripeCoupon) =>
    `${coupon.percentOff}% off · ${months(coupon.durationMonths)}${coupon.name ? ` — ${coupon.name}` : ''}`
/** A date input's YYYY-MM-DD → the end of that day (UTC); empty → no expiry. */
const expiryFromInput = (value: string) => (value ? new Date(`${value}T23:59:59.000Z`).toISOString() : null)
const inputFromExpiry = (iso: string | null) => (iso ? iso.slice(0, 10) : '')
/** Same cadence as the Retention tab while an App Store code batch imports (Apple can take up to an hour). */
const CODES_POLL = { firstMs: 5_000, maxMs: 60_000, giveUpMs: 10 * 60_000 }

const RAILS = [
    { key: 'appleOfferId', label: 'iOS', store: MobileOfferProvider.APPLE },
    { key: 'googleOfferId', label: 'Android', store: MobileOfferProvider.GOOGLE },
    { key: 'stripeCouponId', label: 'Card', store: null },
] as const

/** Create (no `initial`) or edit a code. The code text itself can't change once created. */
export function PromoCodeForm({ initial, onSaved, onCancel }: { initial?: MobilePromoCode; onSaved: (code: MobilePromoCode) => void; onCancel: () => void }) {
    const [code, setCode] = useState(initial?.code ?? '')
    const [plans, setPlans] = useState<Partial<Record<MobilePlan, MobilePromoCodePlanTargets>>>(initial?.plans ?? {})
    const [maxUses, setMaxUses] = useState(initial?.maxRedemptions ? String(initial.maxRedemptions) : '')
    const [expiry, setExpiry] = useState(inputFromExpiry(initial?.expiresAt ?? null))
    const [options, setOptions] = useState<MobilePromoCodePlanOptions[] | null>(null)
    const [saving, setSaving] = useState(false)
    const [generating, setGenerating] = useState<string | null>(null)
    const [error, setError] = useState('')
    const sequence = useRef(0)

    const loadOptions = useCallback(async () => {
        const request = ++sequence.current
        try {
            const result = await mobileOffersApi.promoCodeOptions()
            if (request === sequence.current) setOptions(result)
        } catch (loadError) {
            if (request === sequence.current) setError(mobileOfferError(loadError))
        }
    }, [])
    useEffect(() => {
        void loadOptions()
        return () => {
            sequence.current += 1
        }
    }, [loadOptions])

    const setTarget = (plan: MobilePlan, key: keyof MobilePromoCodePlanTargets, value: string) =>
        setPlans((current) => {
            const next = { ...(current[plan] ?? {}), [key]: value || undefined }
            return { ...current, [plan]: next }
        })
    const forPlan = (plan: MobilePlan) => options?.find((row) => row.plan === plan)
    /** The picked iOS offer's pool, per plan — a code that names an offer with no codes gives iOS users nothing. */
    const pickedApple = (plan: MobilePlan) => {
        const picked = plans[plan]?.appleOfferId
        return picked ? forPlan(plan)?.apple.find((option) => option.offer.id === picked) : undefined
    }
    const codesPending = offerPlans.some((plan) => pickedApple(plan)?.codesPending)
    usePollWhile(codesPending, () => void loadOptions(), CODES_POLL)
    // Same rule as the Retention tab: an App Store offer can't be used until it has codes.
    const appleWithoutCodes = offerPlans.filter((plan) => pickedApple(plan)?.codesAvailable === 0)

    const generateCodes = async (offerId: string) => {
        setGenerating(offerId)
        setError('')
        try {
            await mobileOffersApi.generateAppleCodes(offerId)
            await loadOptions()
        } catch (failure) {
            setError(mobileOfferError(failure))
        } finally {
            setGenerating(null)
        }
    }

    const codeValid = MOBILE_PROMO_CODE_PATTERN.test(code)
    const anyTarget = Object.values(plans).some((targets) => targets && (targets.appleOfferId || targets.googleOfferId || targets.stripeCouponId))
    const maxValid = maxUses === '' || (/^\d+$/.test(maxUses) && Number(maxUses) >= 1)

    const save = async () => {
        setSaving(true)
        setError('')
        // Drop plans with nothing picked so the saved code only lists plans it discounts.
        const cleaned = Object.fromEntries(
            Object.entries(plans).filter(([, targets]) => targets && (targets.appleOfferId || targets.googleOfferId || targets.stripeCouponId))
        )
        const request = { code, plans: cleaned, maxRedemptions: maxUses ? Number(maxUses) : null, expiresAt: expiryFromInput(expiry) }
        try {
            onSaved(initial ? await mobileOffersApi.updatePromoCode(initial.code, request) : await mobileOffersApi.createPromoCode(request))
        } catch (saveError) {
            setError(mobileOfferError(saveError))
        } finally {
            setSaving(false)
        }
    }

    return (
        <section
            aria-label={initial ? `Edit ${initial.code}` : 'Create promo code'}
            className="space-y-5 rounded-xl border border-blue-200 bg-white p-5 shadow-sm dark:border-blue-900 dark:bg-slate-900"
        >
            <div className="grid gap-4 sm:grid-cols-3">
                <label className="text-sm font-medium">
                    Code
                    <input
                        className={`${offerFieldClass} mt-1 font-mono uppercase`}
                        value={code}
                        disabled={!!initial}
                        maxLength={32}
                        placeholder="SUMMER50"
                        onChange={(event) => setCode(event.target.value.toUpperCase().replace(/\s/g, ''))}
                    />
                    {!initial && code && !codeValid && <span className="mt-1 block text-xs text-red-600">3–32 letters, numbers, - or _</span>}
                </label>
                <label className="text-sm font-medium">
                    Max uses (all users)
                    <input
                        className={`${offerFieldClass} mt-1`}
                        inputMode="numeric"
                        value={maxUses}
                        placeholder="Unlimited"
                        onChange={(event) => setMaxUses(event.target.value.trim())}
                    />
                    {!maxValid && <span className="mt-1 block text-xs text-red-600">A whole number, 1 or more</span>}
                </label>
                <label className="text-sm font-medium">
                    Expires
                    <input type="date" className={`${offerFieldClass} mt-1`} value={expiry} onChange={(event) => setExpiry(event.target.value)} />
                </label>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400">
                For each plan, pick what the code gives on each payment method. A method left empty means the code doesn&apos;t work there. Each user can use a
                code once. Whom an offer reaches is shown beside it: a new user gets the discount on the purchase they make, a current subscriber on their next
                renewal (on Android only a retention base-plan offer can reach them). On iOS every use hands out one App Store code from the offer&apos;s pool:
                a new offer gets its first batch on its own, and the pool can be topped up here.
            </p>
            {!options ? (
                <div className="h-32 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800" role="status" aria-label="Loading offers" />
            ) : (
                <div className="space-y-3">
                    {offerPlans.map((plan) => {
                        const row = forPlan(plan)
                        const apple = pickedApple(plan)
                        return (
                            <div key={plan} className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                    <p className="text-sm font-semibold">{planLabels[plan]}</p>
                                    {/* A store with no live offer for the plan needs one made first (same page the Offers tab uses). */}
                                    <Link
                                        to={`/billing/mobile-offers/new?plan=${plan}`}
                                        className={offerLinkClass}
                                        aria-label={`Create a ${planLabels[plan]} store offer`}
                                    >
                                        Create offer
                                        <ArrowRight size={16} />
                                    </Link>
                                </div>
                                <div className="grid gap-3 md:grid-cols-3">
                                    {RAILS.map((rail) => {
                                        const Mark = rail.store ? storeMark[rail.store] : null
                                        const choices =
                                            rail.store === MobileOfferProvider.APPLE
                                                ? (row?.apple ?? []).map(({ offer, codesAvailable, codesPending }) => ({
                                                      id: offer.id,
                                                      label: `${offerLabel(offer)} · ${appleReach(offer)}${codesAvailable ? '' : ` — ${codesPending ? 'generating codes' : 'no codes yet'}`}`,
                                                  }))
                                                : rail.store === MobileOfferProvider.GOOGLE
                                                  ? (row?.google ?? []).map(({ offer, reaches }) => ({
                                                        id: offer.id,
                                                        label: `${offerLabel(offer)} · ${googleReach(reaches)}`,
                                                    }))
                                                  : (row?.coupons ?? []).map((coupon) => ({
                                                        id: coupon.id,
                                                        label: `${couponLabel(coupon)} · new & current subscribers`,
                                                    }))
                                        // Keep a saved pick visible even if it's no longer pickable (saving then fails until it's changed).
                                        const picked = plans[plan]?.[rail.key]
                                        if (picked && !choices.some((choice) => choice.id === picked))
                                            choices.push({ id: picked, label: `${picked} (no longer available)` })
                                        return (
                                            <div key={rail.key} className="text-xs font-medium text-slate-600 dark:text-slate-300">
                                                <label className="block">
                                                    <span className="mb-1 flex items-center gap-2">
                                                        {Mark && <Mark />}
                                                        {rail.label}
                                                    </span>
                                                    <select
                                                        aria-label={`${planLabels[plan]} ${rail.label} discount`}
                                                        className={offerFieldClass}
                                                        value={plans[plan]?.[rail.key] ?? ''}
                                                        disabled={!!generating}
                                                        onChange={(event) => setTarget(plan, rail.key, event.target.value)}
                                                    >
                                                        <option value="">Not on {rail.label}</option>
                                                        {choices.map((choice) => (
                                                            <option key={choice.id} value={choice.id}>
                                                                {choice.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </label>
                                                {/* iOS: the picked offer's code pool, always visible and always top-up-able (like the Retention tab). */}
                                                {rail.store === MobileOfferProvider.APPLE && apple && (
                                                    <div className="mt-2 flex flex-wrap items-center gap-2">
                                                        <CodesChip codes={apple.codesAvailable} pending={apple.codesPending} />
                                                        <button
                                                            type="button"
                                                            className="whitespace-nowrap text-xs font-semibold text-blue-600 hover:underline disabled:opacity-50 dark:text-blue-400"
                                                            disabled={!!generating || saving}
                                                            onClick={() => void generateCodes(apple.offer.id)}
                                                        >
                                                            {generating === apple.offer.id ? 'Generating…' : 'Generate codes'}
                                                        </button>
                                                    </div>
                                                )}
                                                {rail.store === MobileOfferProvider.APPLE && apple && apple.codesAvailable === 0 && (
                                                    <p role="alert" className="mt-2 text-xs font-medium text-amber-700 dark:text-amber-400">
                                                        {apple.codesPending
                                                            ? 'Codes are importing — the code can be saved once they land (Apple can take up to an hour).'
                                                            : 'No App Store codes yet. Generate codes to use this offer for iOS, or choose another offer.'}
                                                    </p>
                                                )}
                                                {/* Card: coupons come from the Promo Codes page, same as the Retention tab's Card row. */}
                                                {rail.store === null && (
                                                    <Link
                                                        to="/promo-codes"
                                                        className={`${offerLinkClass} mt-2`}
                                                        aria-label={`Create a ${planLabels[plan]} coupon on the Promo Codes page`}
                                                    >
                                                        Create coupon
                                                        <ArrowRight size={16} />
                                                    </Link>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}
            {error && <OfferNotice error>{error}</OfferNotice>}
            <div className="flex flex-wrap gap-2">
                <OfferButton
                    disabled={saving || !!generating || !codeValid || !anyTarget || !maxValid || appleWithoutCodes.length > 0}
                    onClick={() => void save()}
                >
                    {saving ? 'Saving…' : initial ? 'Save changes' : 'Create code'}
                </OfferButton>
                <OfferButton variant="ghost" disabled={saving} onClick={onCancel}>
                    Cancel
                </OfferButton>
            </div>
        </section>
    )
}
